/**
 * Emirates ID (UAE national ID) format + check-digit validator.
 *
 * Phase 03 Plan 03-06 Task 1 (EMP-07). Emirates ID is the canonical MENA
 * "GCC_ID" document type in CC-07 — we validate the structural 15-digit
 * shape AND the Luhn-variant check digit at the tail. Other GCC IDs
 * (Bahrain 9 digits, Kuwait 12, Oman 8, etc.) are not currently enforced
 * here; consumers pick `PASSPORT` or `OTHER` for those until backend
 * ships a per-issuer validation endpoint.
 *
 * Format contract (03-RESEARCH §D-10):
 *   - Exactly 15 digits (non-numeric rejected)
 *   - Last digit is a Luhn-variant check computed from the first 14
 *
 * Check-digit algorithm (variant of Luhn/Mod-10):
 *   1. Reverse the first 14 digits.
 *   2. For i = 0..13: if i is even, digit × 2 (if result > 9 subtract 9);
 *      if i is odd, digit unchanged.
 *   3. Sum all transformed digits.
 *   4. Check digit = (10 - (sum mod 10)) mod 10.
 *
 * Rejects: wrong length (14, 16), non-numeric, wrong check digit.
 *
 * MUST-test per project conventions §Testing Philosophy — ID validators are
 * data-integrity critical.
 */
import { z } from "zod";

/** `^\d{15}$` — 15 digits exactly. Check-digit verified in refinement. */
export const EMIRATES_ID_PATTERN = /^\d{15}$/;

/**
 * Compute the Luhn-variant check digit for the first 14 digits of a
 * candidate Emirates ID. Used both by `validateEmiratesCheckDigit` to
 * verify an existing ID and by the test suite to mint valid fixtures.
 *
 * Returns an integer 0..9 regardless of input shape (caller must ensure
 * the 14-char prefix is all digits).
 */
export function computeEmiratesCheckDigit(first14: string): number {
  const digits = first14.split("").map((c) => Number.parseInt(c, 10));
  // Reverse so index 0 is the rightmost of the 14-digit prefix.
  const reversed = digits.slice().reverse();
  let sum = 0;
  for (let i = 0; i < reversed.length; i += 1) {
    let d = reversed[i] ?? 0;
    if (i % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return (10 - (sum % 10)) % 10;
}

/**
 * Verify a candidate 15-digit string against the Emirates-ID check-digit
 * algorithm. Returns `false` for any non-numeric / wrong-length input
 * (defensive — schema regex runs first but refinements can be chained
 * against looser predicate callers).
 */
export function validateEmiratesCheckDigit(id: string): boolean {
  if (!EMIRATES_ID_PATTERN.test(id)) return false;
  const prefix = id.slice(0, 14);
  const providedCheck = Number.parseInt(id.charAt(14), 10);
  return computeEmiratesCheckDigit(prefix) === providedCheck;
}

export const emiratesIdSchema = z
  .string()
  .regex(EMIRATES_ID_PATTERN, {
    message: "Emirates ID must be exactly 15 digits",
  })
  .refine(validateEmiratesCheckDigit, {
    message: "Emirates ID check digit is invalid",
  });

export type EmiratesId = z.infer<typeof emiratesIdSchema>;
